<as-split
  [direction]="'vertical'"
  [unit]="'percent'"
  [gutterSize]="10"
  [useTransition]="true"
  [gutterDblClickDuration]="300"
>
  <as-split-area [size]="40">
    <div class="image-properties-section scrollbar-gutter-stable flex flex-col gap-3 overflow-y-auto p-4">
      <!-- Title Input -->
      <dojo-multi-line-text
        [formCtrl]="titleFormCtrl"
        [size]="'s'"
        [label]="'Title'"
        [placeholder]="'Enter image title (optional)'"
        class="w-full"
      />

      @if (!editingOptions().hideImageAndTableMetadata) {
        <!-- Not to Scale Toggle -->
        <div class="mb-2 mt-2 flex items-center gap-2">
          <mat-slide-toggle [formControl]="notToScaleFormCtrl" class="small" color="primary">
            <span class="text-sm font-semibold">Not to scale</span>
          </mat-slide-toggle>
        </div>

        <!-- Fill in the Blanks Toggle (if provided) -->
        @if (fillInTheBlanksFormCtrl()) {
          <div class="flex items-center gap-2">
            <mat-slide-toggle [formControl]="fillInTheBlanksFormCtrl()!" class="small" color="primary">
              <span class="text-sm font-semibold">Fill in the blanks</span>
            </mat-slide-toggle>
          </div>
        }

        <!-- Height Input -->
        <dojo-number-input
          [size]="'s'"
          [formCtrl]="heightFormCtrl"
          [label]="'Height (px)'"
          [max]="350"
          [min]="1"
          [placeholder]="'Enter height in pixels'"
        ></dojo-number-input>

        <!-- Position Select -->
        <dojo-single-select
          [size]="'s'"
          [label]="'Position'"
          [formCtrl]="positionFormCtrl"
          [optionValue]="'value'"
          [optionLabel]="'label'"
          [options]="positionOptions"
        ></dojo-single-select>

        <!-- Container Layout Select -->
        <dojo-single-select
          [size]="'s'"
          [label]="'Container Width'"
          [formCtrl]="containerLayoutFormCtrl"
          [optionValue]="'value'"
          [optionLabel]="'label'"
          [options]="containerLayoutOptions"
        ></dojo-single-select>
      }
    </div>
  </as-split-area>

  <as-split-area [size]="50" class="flex flex-col">
    <mat-tab-group
      #tabGroup
      mat-stretch-tabs="true"
      mat-align-tabs="start"
      animationDuration="250ms"
      class="custom-mat-tab size-l h-full"
      [class.hide-tab-header]="shouldHideTabHeader()"
      (selectedTabChange)="onTabChange($event)"
    >
      <mat-tab>
        <ng-template mat-tab-label>
          <span>Original</span>
        </ng-template>

        <div class="h-full overflow-y-auto">
          @if (originalImageBlobUrl() || state.originalSignedUrl) {
            <div class="group p-4">
              <div class="mb-2 flex w-full items-center justify-center">
                <span class="badge-above bg-sky-50 uppercase text-sky-600">Original</span>
              </div>
              <div class="flex w-full items-center justify-center">
                <span class="relative inline-block">
                  <img
                    [src]="originalImageBlobUrl() || state.originalSignedUrl!"
                    dojoImagePreview
                    alt="original image"
                    class="w-full"
                  />
                  @if (canEditOriginal()) {
                    <dojo-icon-button
                      class="absolute right-1 top-1 !hidden rounded-[8px] bg-neutral-50 group-hover:!block"
                      [icon]="'times'"
                      (click)="setOriginalImageToFormCtrl(null)"
                    />
                  }
                </span>
              </div>
            </div>
          } @else {
            @if (canEditOriginal()) {
              <div class="h-full p-2">
                <dojo-image-dropzone
                  [hideIcon]="true"
                  [accept]="'image/jpeg, image/png, image/heic'"
                  (imageSelected)="setOriginalImageToFormCtrl($event)"
                  class="h-full"
                />
              </div>
            }
          }
        </div>
      </mat-tab>

      @if (canEditGenerated()) {
        <mat-tab>
          <ng-template mat-tab-label>
            <span>Code</span>
          </ng-template>

          <div class="relative flex h-full flex-col gap-4">
            <div class="flex-1">
              <dojo-python-editor
                [formCtrl]="codeFormCtrl"
                [keyboardBindings]="keyboardBindings"
                (keyboardEvents)="onKeyBoardEvents($event)"
              />
            </div>
            <div class="absolute bottom-[23px] right-[15px] z-[5]">
              <dojo-button
                [fill]="'solid'"
                [customColor]="'sky'"
                [size]="'xs'"
                [disabled]="!state.isCodeFormCtrlValueValid"
                [pending]="imageFromCodeLoading()"
                (click)="generateNewImage()"
              >
                Run (Ctrl + Y)
              </dojo-button>
            </div>
          </div>
        </mat-tab>

        <mat-tab>
          <ng-template mat-tab-label>
            <span>Upload</span>
          </ng-template>

          <div class="h-full overflow-y-auto">
            @if (manuallyUploadedImageBlobUrl()) {
              <div class="group">
                <span class="relative inline-block p-4">
                  <img [src]="manuallyUploadedImageBlobUrl()" dojoImagePreview alt="image" class="h-[150px]" />
                  <dojo-icon-button
                    class="absolute right-1 top-1 !hidden rounded-[8px] bg-neutral-50 group-hover:!block"
                    [icon]="'times'"
                    (click)="setManuallyUploadedImageToFormCtrl(null)"
                  />
                </span>
              </div>
            } @else {
              <div class="h-full p-2">
                <dojo-image-dropzone
                  [hideIcon]="true"
                  [accept]="'image/jpeg, image/png, image/heic'"
                  (imageSelected)="setManuallyUploadedImageToFormCtrl($event)"
                  class="h-full"
                />
              </div>
            }
          </div>
        </mat-tab>

        @if (imageChat) {
          <mat-tab>
            <ng-template mat-tab-label>
              <span>Chat</span>
            </ng-template>

            <div class="h-full overflow-y-auto">
              <dojo-image-chat [contentJsonPath]="contentJsonPath()" class="min-h-[200px]" />
            </div>
          </mat-tab>
        }
      }

      @if (state.formCtrlValue.description) {
        <mat-tab>
          <ng-template mat-tab-label>
            <span>Description</span>
          </ng-template>

          <div class="flex flex-col p-4">
            <dojo-markdown-viewer [content]="state.formCtrlValue.description" />
          </div>
        </mat-tab>
      }
    </mat-tab-group>
  </as-split-area>
</as-split>
